import { EpubCFI } from "epubjs";
import { eq } from "./CommonUtils";
import { isTextNode, isElementNode, isEmptyElement } from "./DomUtils";

interface CfiStep {
  id: string;
  tagName: string;
  type: string;
  index: number;
}

interface CfiTerminal {
  offset: number | null;
  assertion: string | null;
}

interface CfiComponent {
  steps: CfiStep[];
  terminal: CfiTerminal;
}

/**
 * EpubCFI exposes the parsed `path`, `start`, and `end` components at runtime, but
 * epubjs' type definitions omit them. This view describes the parts we manipulate.
 */
type ParsedEpubCFI = EpubCFI & {
  path: CfiComponent;
  start: CfiComponent;
  end: CfiComponent;
};

const EMPTY_TERMINAL: CfiTerminal = { offset: null, assertion: null };

/**
 * Converts a Range into a CFI, properly accounting for boundaries in element containers.
 */
export function cfiFromRange(
  range: Range,
  base?: string | object,
  ignoreClass?: string,
): string {
  // Attempt to adjust the endpoints to be epubcfi-friendly.
  const r = adjustRangeEndpoints(range);

  const cfi = new EpubCFI(r, base, ignoreClass) as ParsedEpubCFI;

  const endPoints: [Node, number, CfiComponent][] = [];

  if (cfi.range) {
    endPoints.push([r.startContainer, r.startOffset, cfi.start]);
    endPoints.push([r.endContainer, r.endOffset, cfi.end]);
  } else {
    endPoints.push([r.endContainer, r.endOffset, cfi.path]);
  }

  endPoints.forEach((e) => {
    const container = e[0];
    const offset = e[1];
    const path = e[2];

    if (isElementNode(container)) {
      // At this point, only empty elements could end up with an element node container.
      const node = container.childNodes[offset];

      // Pop off the faux text node, push this one in.
      path.steps.pop();
      path.steps.push(step(node as Element));
      path.terminal = EMPTY_TERMINAL;
    }
  });

  const adjustedCfi = cfi.toString();
  return adjustedCfi;
}

/**
 * Returns true if `inner`'s start point lies within `outer`'s bounds (inclusive).
 * Returns false if either range is missing or the ranges have different owner documents.
 */
export function rangeStartsWithin(inner: Range, outer: Range): boolean {
  if (!rangesExist(inner, outer)) return false;
  if (!rangeOwnersMatch(inner, outer)) return false;

  return (
    inner.compareBoundaryPoints(Range.START_TO_START, outer) >= 0 &&
    inner.compareBoundaryPoints(Range.END_TO_START, outer) <= 0
  );
}

/**
 * Returns true if two DOM Ranges share any common span.
 */
export function rangesOverlap(a: Range, b: Range): boolean {
  if (!rangesExist(a, b)) return false;
  if (!rangeOwnersMatch(a, b)) return false;

  return rangeStartsWithin(a, b) || rangeStartsWithin(b, a);
}

/**
 * Returns true if `inner`'s start point lies at or before `container`'s end (in tree order).
 * Useful for "has TTS crossed this point" checks where a bookmark may sit upstream
 * of the current sentence in the DOM.
 * Returns false if either range is missing or if the ranges have different owner documents.
 */
export function rangeStartsAtOrBefore(inner: Range, container: Range): boolean {
  if (!rangesExist(inner, container)) return false;
  if (!rangeOwnersMatch(inner, container)) return false;

  return inner.compareBoundaryPoints(Range.END_TO_START, container) <= 0;
}

/**
 * Returns true if `inner`'s start point lies strictly before `container`'s start (in tree order).
 * Returns false if either range is missing or if the ranges have different owner documents.
 */
export function rangeStartsBefore(inner: Range, container: Range): boolean {
  if (!rangesExist(inner, container)) return false;
  if (!rangeOwnersMatch(inner, container)) return false;

  return inner.compareBoundaryPoints(Range.START_TO_START, container) < 0;
}

/**
 * Returns true if neither `a` nor `b` are falsy, otherwise returns false.
 */
export function rangesExist(
  a: Range | null | undefined,
  b: Range | null | undefined,
): boolean {
  let bothRangesExist = true;
  if (!a || !b) bothRangesExist = false;
  return bothRangesExist;
}

/**
 * Returns true if both `a` and `b` share an ownerDocument, otherwise returns false.
 */
export function rangeOwnersMatch(a: Range, b: Range): boolean {
  let rangesMatch = true;
  if (a.startContainer.ownerDocument !== b.startContainer.ownerDocument) {
    rangesMatch = false;
  }
  return rangesMatch;
}

/**
 * Patches Ranges from Rendition.getRange() that point to a zero child of a childless element.
 */
export function patchRange(
  range: Range | null | undefined,
): Range | null | undefined {
  if (range) {
    const patchedRange = range.cloneRange();

    if (
      isEmptyElement(patchedRange.startContainer) &&
      patchedRange.startOffset === 0
    ) {
      patchedRange.setStartBefore(patchedRange.startContainer);
    }

    if (
      isEmptyElement(patchedRange.endContainer) &&
      patchedRange.endOffset === 0
    ) {
      patchedRange.setEndBefore(patchedRange.endContainer);
    }

    return patchedRange;
  }
  return range;
}

/**
 * Given two CFIs, generates a CFI range that encompasses the earliest and latest
 * points from both CFIs. Endpoints a and b do not need to be provided in document order,
 * and they may be single locations or ranges.
 * @param a CFI endpoint a
 * @param b CFI endpoint b
 * @return CFI string if there is a legal range between the two CFIs.
 */
export function cfiRangeFromCfiPair(a: string, b: string): string | null {
  // Return value
  const cfi = new EpubCFI() as ParsedEpubCFI;

  // Determine the endpoints.
  const aStart = new EpubCFI(a) as ParsedEpubCFI;
  const bStart = new EpubCFI(b) as ParsedEpubCFI;
  const aEnd = new EpubCFI(a) as ParsedEpubCFI;
  const bEnd = new EpubCFI(b) as ParsedEpubCFI;

  // Bail if the CFIs aren't even from the same base document.
  if (!eq(aStart.base, bStart.base)) {
    return null;
  }

  // Collapse to starts and ends for comparison. The collapse() function mutates the CFIs.
  aStart.collapse(true);
  bStart.collapse(true);
  aEnd.collapse();
  bEnd.collapse();

  // Pick the earliest start point and the latest end point.
  const start = cfi.compare(aStart, bStart) < 0 ? aStart : bStart;
  const end = cfi.compare(aEnd, bEnd) > 0 ? aEnd : bEnd;

  // Initialize the new cfi
  cfi.base = Object.assign({}, start.base);
  cfi.path.steps = [];
  cfi.path.terminal = EMPTY_TERMINAL;
  cfi.range = true;

  // Accumulate shared steps. Break when they stop matching OR if we encounter a shared text step.
  let i = 0;
  while (i < start.path.steps.length) {
    const startStep = start.path.steps[i];
    const endStep = end.path.steps[i];
    if (
      startStep &&
      endStep &&
      cfi.equalStep(startStep, endStep) &&
      startStep.type !== "text"
    ) {
      cfi.path.steps.push(startStep);
      i++;
    } else {
      break;
    }
  }

  // Place remainders into start/end paths
  cfi.start = {
    steps: start.path.steps.slice(i),
    terminal: start.path.terminal,
  };

  cfi.end = {
    steps: end.path.steps.slice(i),
    terminal: end.path.terminal,
  };

  return cfi.toString();
}

/**
 * Given a CFI range, return an object containing pair of CFIs corresponding to the
 * range's endpoints. If given a non-range CFI, both start and end will be the same CFI.
 * @param cfi CFI string
 * @returns Simple object containing start/end CFIs
 */
export function cfiPairFromCfiRange(cfi: string): {
  start: string;
  end: string;
} {
  // Make some new CFI objects
  const start = new EpubCFI(cfi);
  const end = new EpubCFI(cfi);

  if (start.range) {
    start.collapse(true);
    end.collapse();
  }

  return {
    start: start.toString(),
    end: end.toString(),
  };
}

const isWordCharacter = (character: string | undefined): boolean =>
  character !== undefined && !/\s/.test(character);

/**
 * Snaps both endpoints of a selection Range onto word boundaries in place. An endpoint that
 * cuts a word grows outward to cover the whole word; an endpoint sitting in whitespace snaps
 * inward to the adjacent word, dropping the whitespace. A word is a maximal run of
 * non-whitespace characters, so any punctuation attached to it (trailing periods, wrapping
 * parentheses, apostrophes, hyphens) is kept with the word. A selection containing no word at
 * all (only whitespace) collapses.
 * @param range Selection range to adjust in place.
 * @returns The same range, for convenience.
 */
export function expandRangeToWholeWords(range: Range): Range {
  if (range.collapsed) {
    return range;
  }

  if (isTextNode(range.startContainer)) {
    const text = range.startContainer.textContent ?? "";
    let offset = range.startOffset;
    if (isWordCharacter(text[offset])) {
      // The selection starts inside a word: grow left to the word's start.
      while (offset > 0 && isWordCharacter(text[offset - 1])) {
        offset--;
      }
    } else {
      // The selection starts on whitespace: snap right to the next word's start.
      while (offset < text.length && !isWordCharacter(text[offset])) {
        offset++;
      }
    }
    range.setStart(range.startContainer, offset);
  }

  if (isTextNode(range.endContainer)) {
    const text = range.endContainer.textContent ?? "";
    let offset = range.endOffset;
    if (isWordCharacter(text[offset - 1])) {
      // The selection ends inside a word: grow right to the word's end.
      while (offset < text.length && isWordCharacter(text[offset])) {
        offset++;
      }
    } else {
      // The selection ends on whitespace: snap left to the previous word's end.
      while (offset > 0 && !isWordCharacter(text[offset - 1])) {
        offset--;
      }
    }
    range.setEnd(range.endContainer, offset);
  }

  return range;
}

/**
 * Creates a CFI step-shaped object from the provided Element.
 */
function step(element: Element): CfiStep {
  return {
    id: element.id,
    tagName: element.tagName,
    type: "element",
    index: [...element.parentElement!.children].indexOf(element),
  };
}

/**
 * Finds the next text or empty (childless element) node that follows this one in the DOM.
 * @returns The next text or empty node in DOM order, or null if none could be found.
 */
function findNextTextOrEmptyNodeInDomOrder(node: Node): Node | null {
  // Check the most likely suspect first, to avoid unnecessary TreeWalker creation.
  if (
    node.hasChildNodes() &&
    (isTextNode(node.firstChild) || isEmptyElement(node.firstChild))
  ) {
    return node.firstChild;
  } else {
    const walker = createDomWalker(node);
    walker.currentNode = node;
    return walker.nextNode();
  }
}

/**
 * Finds the closest preceding text or empty (childless element) node, .
 * @returns The closest text or empty node that precedes this one in DOM order, null if none is found.
 */
function findNextTextOrEmptyNodeInReverseDomOrder(node: Node): Node | null {
  // Check the most likely suspect first, to avoid unnecessary TreeWalker creation.
  // Drill down to the deepest lastChild. DOM order is element, then contents, so when going
  // backwards we need to start from the node's contents.
  let walkerOrigin: Node = node;
  while (walkerOrigin.hasChildNodes()) {
    walkerOrigin = walkerOrigin.lastChild!;
  }

  if (isTextNode(walkerOrigin) || isElementNode(walkerOrigin)) {
    return walkerOrigin;
  } else {
    const walker = createDomWalker(node);
    walker.currentNode = walkerOrigin;
    return walker.previousNode();
  }
}

/**
 * Adjusts range endpoints. We want round-trips from Range to CFI and back to be as consistent
 * as possible, so our first step is to adjust the endpoints so that they are as close as possible
 * to what epubcfi.js wants while still retaining the text content of the original Range.
 * For all element anchor containers:
 * - Offsets that reference a text node are converted to text node references with a zero char offset.
 * - Offsets that reference an element node with children are converted to a reference for either
 *   the next text node in DOM order with a zero char offset or empty element node.
 * - Offsets that reference an element node with no children (empty nodes) are left alone
 * - Offsets after last node child, where last node child is text, are converted to text node reference
 *   with a char offset equal to the length of the text node.
 * - Offsets after last node child, where last node child is an element, are converted to either
 *   a text node reference for the next text node in DOM order with a zero char offset or empty
 *   element node.
 */
export function adjustRangeEndpoints(range: Range): Range {
  const r = range.cloneRange();

  type Endpoint = [
    Node,
    number,
    (node: Node) => void,
    (node: Node, offset: number) => void,
  ];

  const endpoints: Endpoint[] = [
    [r.startContainer, r.startOffset, r.setStartBefore, r.setStart],
    [r.endContainer, r.endOffset, r.setEndBefore, r.setEnd],
  ];

  endpoints.forEach((e) => {
    const container = e[0];
    const offset = e[1];
    const setBefore = e[2];
    const set = e[3];

    if (isElementNode(container)) {
      // find the referenced node
      let node: Node | null | undefined = container.childNodes[offset];

      if (isTextNode(node)) {
        // If it's text, easy peasy. Use the 0 index.
        set.call(r, node, 0);
      } else if (isEmptyElement(node)) {
        // Empty elements are also easy
        setBefore.call(r, node);
      } else if (isElementNode(node)) {
        // Element with children, find the next available text or empty node
        node = findNextTextOrEmptyNodeInDomOrder(node);
        if (isEmptyElement(node)) {
          setBefore.call(r, node);
        } else if (isTextNode(node)) {
          set.call(r, node, 0);
        }
      } else {
        // Yuck, we're at a last child + 1 situation.
        if (isTextNode(container.lastChild)) {
          // Simple case: use the end of the text node.
          node = container.lastChild;
        } else if (isElementNode(container.lastChild)) {
          // Tougher case, we have to search backwards for the last usable point
          node = findNextTextOrEmptyNodeInReverseDomOrder(container.lastChild);
        }

        if (isTextNode(node)) {
          set.call(r, node, node.length);
        } else if (isEmptyElement(node)) {
          // Not ideal, we may be off by 1 empty element.
          setBefore.call(r, node);
        }
      }
    }
  });

  return r;
}

/**
 * Creates a DOM walker for the given node's document that looks for text or empty element nodes.
 */
function createDomWalker(node: Node): TreeWalker {
  return node.ownerDocument!.createTreeWalker(
    node.ownerDocument!.body,
    NodeFilter.SHOW_TEXT |
      NodeFilter.SHOW_CDATA_SECTION |
      NodeFilter.SHOW_ELEMENT,
    (n) =>
      isTextNode(n) || isEmptyElement(n)
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT,
  );
}
